import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileCheck, Sliders, Key, MessageCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const h2Style: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 'clamp(26px, 3.5vw, 38px)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  lineHeight: 1.2,
  letterSpacing: '-0.025em',
};

const Home = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Helmet>
        <title>BrightWings — Clear Technical Direction for Founders</title>
        <meta name="description" content="We help founders navigate technical decisions with clarity — figuring out what to build, scoping it precisely, and delivering without drama." />
        <link rel="canonical" href="https://brightwings.io/" />
        <meta property="og:url" content="https://brightwings.io/" />
        <meta property="og:title" content="BrightWings — Clear Technical Direction for Founders" />
        <meta property="og:description" content="We help founders navigate technical decisions with clarity — figuring out what to build, scoping it precisely, and delivering without drama." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="BrightWings — Clear Technical Direction for Founders" />
        <meta name="twitter:description" content="We help founders navigate technical decisions with clarity — figuring out what to build, scoping it precisely, and delivering without drama." />
      </Helmet>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="relative z-10" style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div className="flex justify-center">
            <span className="bw-eyebrow-pill">BrightWings.io</span>
          </div>

          <div className="flex justify-center" style={{ marginBottom: '24px' }}>
            <img
              src="/brightwings-icon.svg?v=2"
              alt="BrightWings"
              className="float-animation"
              style={{ width: '64px', height: '52px' }}
            />
          </div>

          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(36px, 5vw, 58px)',
            fontWeight: 800,
            color: 'var(--bw-dark)',
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            marginBottom: '20px',
          }}>
            You have a vision.{' '}
            <br />
            Building it shouldn't feel{' '}
            <em style={{ color: 'var(--bw-blue)', fontStyle: 'normal' }}>this hard.</em>
          </h1>

          <p style={{
            fontSize: '18px',
            color: 'var(--bw-text-mid)',
            lineHeight: 1.65,
            maxWidth: '580px',
            margin: '0 auto 32px',
          }}>
            We help founders navigate technical decisions with clarity — figuring out what to build, scoping it precisely, and delivering without drama.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
              Start a conversation
            </Link>
            <a href="#how-we-work" className="bw-btn-secondary">
              How we work
            </a>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="bw-stats-bar">
        {[
          { val: '15+', lbl: 'Years Experience' },
          { val: '50+', lbl: 'Founders Helped' },
          { val: '100%', lbl: 'Founder Ownership' },
        ].map((stat, i) => (
          <div key={i} className="bw-stat-item">
            <div className="bw-stat-val">{stat.val}</div>
            <div className="bw-stat-lbl">{stat.lbl}</div>
          </div>
        ))}
      </div>

      {/* Problem + Outcome */}
      <section className="bw-section-white">
        <div style={{ maxWidth: '1040px', margin: '0 auto' }}>
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            <span className="bw-eyebrow">The Problem</span>
            <h2 style={{ ...h2Style, marginBottom: '16px' }}>
              Founders don't fail because of vision.
            </h2>
            <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', lineHeight: 1.65, marginBottom: '28px' }}>
              They fail because they can't confidently direct technical execution — so they struggle to evaluate developers, feel dependent on whoever's building, and make decisions without understanding the tradeoffs.
            </p>
          </div>

          <div style={{ maxWidth: '680px', margin: '0 auto' }}>
            <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--bw-text)', marginBottom: '16px' }}>With BrightWings, instead:</p>
            <ul style={{ listStyle: 'none' }}>
              {[
                'You build with clarity, not guesswork.',
                'You scale with intention, not luck.',
                'You own every decision — and every line of code.',
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
                    <circle cx="10" cy="10" r="10" fill="var(--bw-blue)" />
                    <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span style={{ fontSize: '16px', fontWeight: 500, color: 'var(--bw-text)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-we-work" className="bw-section-surface">
        <div style={{ maxWidth: '1040px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>
            <span className="bw-eyebrow" style={{ textAlign: 'center' }}>The Process</span>
            <h2 style={{ ...h2Style, textAlign: 'center' }}>Simple, honest process.</h2>
          </div>

          <div className="bw-process-steps">
            {[
              {
                step: '1',
                title: 'You describe the pain',
                description: "Don't worry about technical details. Just tell us what's broken, slow, or costing you time. We'll ask the right questions to understand your business reality.",
              },
              {
                step: '2',
                title: 'We translate pain into clear decisions',
                description: 'Figure out what to build, scope it clearly, understand the tradeoffs. Blueprint, advisory, or full build: you choose what fits your needs and budget.',
              },
              {
                step: '3',
                title: 'We build it right, if you choose',
                description: 'Weekly updates, working software you can test, honest communication. We focus on craftsmanship, reliability, and long-term thinking — not shortcuts.',
              },
            ].map((step, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '0 32px' }}>
                <div className="bw-step-circle">{step.step}</div>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--bw-blue)',
                  marginBottom: '8px',
                }}>Step {step.step}</div>
                <h3 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '20px',
                  fontWeight: 800,
                  color: 'var(--bw-dark)',
                  letterSpacing: '-0.02em',
                  marginBottom: '10px',
                }}>{step.title}</h3>
                <p style={{ fontSize: '14px', color: 'var(--bw-muted)', lineHeight: 1.65 }}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guide + Why Us */}
      <section className="bw-section-white">
        <div style={{ maxWidth: '1040px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'start' }} className="md:grid-cols-2">
            <div>
              <span className="bw-eyebrow">Why BrightWings</span>
              <h2 style={{ ...h2Style, marginBottom: '20px' }}>
                Not a vendor. Not a consultant.{' '}
                <em style={{ color: 'var(--bw-blue)', fontStyle: 'normal' }}>A partner with skin in the outcome.</em>
              </h2>
              <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.65 }}>
                We've worked with founders from every background. The struggle is always the same: brilliant idea, no roadmap. BrightWings exists to be the guide you needed from the start.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                {
                  Icon: FileCheck,
                  title: 'Decisions before code',
                  description: "We don't write a line of code until the scope is clear and you understand what you're building and why.",
                },
                {
                  Icon: Sliders,
                  title: 'Flexibility at every step',
                  description: "Blueprint only. Blueprint plus advisory. Full build. You choose what fits — we don't push toward the largest engagement.",
                },
                {
                  Icon: Key,
                  title: 'You own everything',
                  description: "Code, architecture, docs — all yours. Take it anywhere when we're done. No lock-in, ever.",
                },
                {
                  Icon: MessageCircle,
                  title: "Honest, even when it's uncomfortable",
                  description: "We'd rather have a hard conversation early than cost you months of rework later.",
                },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div className="bw-feature-icon flex-shrink-0" style={{ marginBottom: 0 }}>
                    <item.Icon size={18} color="var(--bw-blue)" />
                  </div>
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: 700, color: 'var(--bw-dark)', marginBottom: '4px' }}>{item.title}</h3>
                    <p style={{ fontSize: '13px', color: 'var(--bw-muted)', lineHeight: 1.6 }}>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Not For You */}
      <section className="bw-section-dark">
        <div style={{ maxWidth: '1040px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="bw-eyebrow" style={{ color: 'rgba(0, 163, 224, 0.9)', textAlign: 'center' }}>Honest Fit Check</span>
            <h2 style={{ ...h2Style, color: 'white', textAlign: 'center' }}>
              This is <em style={{ color: 'var(--bw-blue)', fontStyle: 'normal' }}>not</em> for you if:
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {[
              {
                title: 'You want the cheapest option',
                description: "We build production-ready software that lasts. If you're optimizing purely for lowest cost rather than quality and reliability, we're not the right fit.",
              },
              {
                title: 'You just want to brainstorm',
                description: "We provide decision clarity that leads to action. If you're not ready to move forward with real software decisions, we're probably not the right fit yet.",
              },
              {
                title: 'You need a strategy consultant',
                description: "We connect business problems to technical solutions. If you need high-level strategy work disconnected from execution, we're not the right fit.",
              },
            ].map((item, i) => (
              <div key={i} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '14px',
                padding: '24px',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <span style={{ fontSize: '22px', fontWeight: 700, color: 'var(--bw-blue)', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}>×</span>
                  <div>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: 'white',
                      marginBottom: '8px',
                    }}>{item.title}</h3>
                    <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.55)', lineHeight: 1.65 }}>{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bw-cta-section">
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <span className="bw-eyebrow" style={{ textAlign: 'center' }}>Get Started</span>
          <h2 style={{ ...h2Style, textAlign: 'center', marginBottom: '16px' }}>
            Ready to build with{' '}
            <em style={{ color: 'var(--bw-blue)', fontStyle: 'normal' }}>direction</em>?
          </h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', lineHeight: 1.65, marginBottom: '36px' }}>
            Let's have a real conversation about what you're trying to build. We'll get clear on the problem, the direction, and whether it makes sense to work together.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
              Start a conversation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
